import { Link } from "react-router-dom";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "accent" | "secondary" | "outline" | "ghost" | "light";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: string;
  iconRight?: string;
  to?: string;
  href?: string;
  fullWidth?: boolean;
  children?: ReactNode;
}

const base =
  "inline-flex items-center justify-center gap-2 font-label font-semibold whitespace-nowrap rounded-md transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-background-50 disabled:opacity-50 disabled:cursor-not-allowed";

const variants: Record<Variant, string> = {
  primary: "bg-primary-500 text-background-50 hover:bg-primary-600 dark:text-foreground-950",
  accent: "bg-accent-500 text-background-50 hover:bg-accent-600 dark:text-foreground-950",
  secondary: "bg-secondary-100 text-secondary-900 hover:bg-secondary-200",
  outline:
    "border border-background-300 text-foreground-800 hover:bg-background-100 hover:border-primary-300",
  ghost: "text-foreground-700 hover:bg-background-100 hover:text-foreground-950",
  light: "bg-background-50 text-foreground-950 hover:bg-background-100",
};

const sizes: Record<Size, string> = {
  sm: "text-sm px-3.5 py-2",
  md: "text-sm px-5 py-2.5",
  lg: "text-base px-6 py-3.5",
};

export default function Button({
  variant = "primary",
  size = "md",
  icon,
  iconRight,
  to,
  href,
  fullWidth = false,
  children,
  className = "",
  ...rest
}: ButtonProps) {
  const classes = [base, variants[variant], sizes[size], fullWidth ? "w-full" : "", className]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {icon && <i className={`${icon} text-base leading-none`} aria-hidden="true"></i>}
      {children}
      {iconRight && <i className={`${iconRight} text-base leading-none`} aria-hidden="true"></i>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} className={classes}>
        {content}
      </a>
    );
  }

  return (
    <button className={classes} {...rest}>
      {content}
    </button>
  );
}